import React from 'react';
import { SiteSettings, Language } from '../types';

interface FooterProps {
  settings: SiteSettings;
  language: Language;
}

export const Footer: React.FC<FooterProps> = ({ settings, language }) => {
  // Use config link or default search/frontpage if not configured so the app is always highly functional
  const deviantart = settings.deviantartUrl || 'https://www.deviantart.com';
  const telegram = settings.telegramUrl || 'https://t.me';
  const discord = settings.discordUrl || 'https://discord.com';

  return (
    <footer id="main-footer" className="w-full mt-32 border-t border-white/5 pt-16 pb-12 animate-in fade-in duration-1000">
      <div className="max-w-4xl mx-auto flex flex-col items-center justify-center space-y-10 px-6 text-center">
        
        {/* Fine Art Accent */}
        <div className="flex items-center gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-[#c084fc] animate-ping" />
          <span className="text-[9px] font-black uppercase text-zinc-500 tracking-[0.6em]">
            {language === 'en' ? 'Santuário Connection Hub' : 'Portal de Conexão do Santuário'}
          </span>
          <div className="w-1.5 h-1.5 rounded-full bg-[#c084fc] animate-ping" />
        </div>

        {/* Title */}
        <div className="space-y-3">
          <h4 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight font-sans">
            {language === 'en' ? 'CONNECT WITH THE CRAFT' : 'CONECTE-SE COM O OFÍCIO'}
          </h4>
          <p className="text-[10px] md:text-xs text-zinc-400 font-medium tracking-wide max-w-md mx-auto leading-relaxed">
            {language === 'en' 
              ? 'Join our outer communities to discuss personalized profile decorations, custom illustrations, and live animated artwork.' 
              : 'Entre em nossas comunidades externas para discutir artes personalizadas, ilustrações exclusivas e animações sob medida.'}
          </p>
        </div>

        {/* Buttons Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-lg pt-4">
          
          {/* DeviantArt Button */}
          <a
            id="footer-deviantart-btn"
            href={deviantart}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 bg-[#050505] hover:bg-zinc-900 border border-zinc-800 hover:border-[#4dc247]/50 text-white hover:text-[#4dc247] px-6 py-4 text-[10px] font-black uppercase tracking-widest transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 duration-300 relative overflow-hidden group border-b-2"
          >
            <div className="absolute inset-0 bg-[#4dc247]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current transition-transform group-hover:scale-110" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.86 2.33v4.4l3.18 3.22H4.86v5.52h2.24l3.18-5.52v-4.4l-3.18-3.22h3.18V2.33H4.86zm14.28 19.34v-4.4l-3.18-3.22h3.18v-5.52h-2.24l-3.18 5.52v4.4l3.18 3.22h-3.18v3.42h7.42z" />
            </svg>
            <span>DeviantArt</span>
          </a>

          {/* Telegram Button */}
          <a
            id="footer-telegram-btn"
            href={telegram}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 bg-[#050505] hover:bg-zinc-900 border border-zinc-800 hover:border-[#229ED9]/50 text-white hover:text-[#229ED9] px-6 py-4 text-[10px] font-black uppercase tracking-widest transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 duration-300 relative overflow-hidden group border-b-2"
          >
            <div className="absolute inset-0 bg-[#229ED9]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current transition-transform group-hover:scale-110" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.25-5.54 3.69-.52.36-1 .53-1.42.52-.47-.01-1.37-.26-2.03-.48-.82-.27-1.47-.42-1.42-.88.08-.47.72-.73 1.94-1.22 3.79-1.65 6.32-2.73 7.59-3.25 3.61-1.48 4.36-1.74 4.85-1.75.11 0 .35.03.5.16.13.12.16.29.18.41v.3z" />
            </svg>
            <span>Telegram</span>
          </a>

          {/* Discord Button */}
          <a
            id="footer-discord-btn"
            href={discord}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 bg-[#050505] hover:bg-zinc-900 border border-zinc-800 hover:border-[#5865F2]/50 text-white hover:text-[#5865F2] px-6 py-4 text-[10px] font-black uppercase tracking-widest transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 duration-300 relative overflow-hidden group border-b-2"
          >
            <div className="absolute inset-0 bg-[#5865F2]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current transition-transform group-hover:scale-110" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.46-.627.87-1.3 1.226-2.01a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.92 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.099.244.197.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.707.7 1.38 1.225 2.01a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.156-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.156 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.156-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.156 2.418z" />
            </svg>
            <span>Discord</span>
          </a>

        </div>

        {/* Small copyright disclaimer */}
        <div className="text-[8px] text-zinc-650 font-black uppercase tracking-[0.4em] pt-4">
          &copy; 2026 Steam Niiko Artwork. ALL RIGHTS RESERVED.
        </div>

      </div>
    </footer>
  );
};
